import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface TipoServicoType {
    id: number;
    name: string;    
    value: number;
    empresaId: number;
}

export interface CreateTipoServicoType {
    name: string;
    value: number;
    empresaId: number;
}

export interface UpdateTipoServicoType {
    id: number;
    name: string;
    value: number;
}

export default function useTipoServico() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listTipoServico, setListTipoServico] = useState<TipoServicoType[]>([]);
    const [pageCount, setPageCount] = useState<number>(0);
    const [initialListTipoServico, setInitialListTipoServico] = useState<TipoServicoType[]>([]);
    const [tipoServico, setTipoServico] = useState<TipoServicoType>({
        id: 0,
        name: "",
        value: 0,
        empresaId: 0,
    });

    function createTipoServico(createTipoServicoType: CreateTipoServicoType): Promise<CreateTipoServicoType | null> {
        setLoading(true);
        return api
            .post(`/TipoServico/create`, createTipoServicoType)
            .then((response) => {
                const createdTipoServico: CreateTipoServicoType = response.data;
                showToast("success", "Tipo de terceiro criada com sucesso.");
                setLoading(false);
                return createdTipoServico;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar tipo de serviço. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar tipo de serviço:", error);
                return null;
            });
    }

    function getAllTipoServicoPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<TipoServicoType[] | null> {
        setLoading(true);
        let url = `/TipoServico/getAllTipoServicoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListTipoServico(response.data.result);
                setInitialListTipoServico(response.data.result);
                setPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar tipo de serviço. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar tipo de serviço:", error);
                return null;
            });
    }

    function listTipoServicoByEmpresa(empresaId: number): Promise<TipoServicoType[] | null> {
        setLoading(true);
        return api
            .get(`/TipoServico/list-by-empresa?empresaId=${empresaId}`)
            .then((response) => {
                const list = response.data as TipoServicoType[];
                setListTipoServico(list);
                setInitialListTipoServico(list);
                setLoading(false);
                return list;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao listar serviços da empresa.", error);
                }
                setLoading(false);
                console.error("Erro ao listar serviços da empresa:", error);
                return null;
            });
    }

    function updateTipoServico(updateTipoServicoData: UpdateTipoServicoType): Promise<UpdateTipoServicoType | null> {
        setLoading(true);
        return api
            .put(`/TipoServico/${updateTipoServicoData.id}`, updateTipoServicoData)
            .then((response) => {
                showToast("success", "Tipo de serviço atualizado com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar tipo de serviço. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar tipo de serviço:", error);
                return null;
            });
    }

    function deleteTipoServico(tipoServicoId: number): Promise<TipoServicoType | null> {
        setLoading(true);
        return api
            .delete(`/TipoServico/${tipoServicoId}`)
            .then(() => {
                setTipoServico({
                    id: 0,
                    name: "",
                    value: 0,
                    empresaId: 0,
                });
                showToast("success", "Tipo de serviço deletado com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar tipo de serviço. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar tipo de serviço:", error);
                return null;
            });
    }

    return {
        createTipoServico,
        getAllTipoServicoPaged,
        updateTipoServico,
        deleteTipoServico,
        setListTipoServico,
        listTipoServico,
        setPageCount,
        pageCount,
        setInitialListTipoServico,
        initialListTipoServico,
        setTipoServico,
        tipoServico,
        listTipoServicoByEmpresa,
    };
}